/**
 * Las cuentas chicas que usan los tres instrumentos de Yichang.
 */

export interface Conteo { valor: string; n: number }

/** Cuántas veces aparece cada valor, de más a menos y después en orden alfabético. */
export function contar(xs: readonly string[]): Conteo[] {
  const m = new Map<string, number>();
  for (const x of xs) m.set(x, (m.get(x) ?? 0) + 1);
  return [...m.entries()].map(([valor, n]) => ({ valor, n }))
    .sort((a, b) => b.n - a.n || a.valor.localeCompare(b.valor, 'es'));
}

/**
 * Cuenta en un ORDEN dado (el del formulario), y al final lo que no estaba en la lista. Sirve para
 * que «Completa · A medias · No existe…» se lea siempre igual y no según cuál ganó hoy.
 */
export function contarEnOrden(xs: readonly string[], orden: readonly string[]): Conteo[] {
  const base = contar(xs);
  const enOrden = orden.map((valor) => ({ valor, n: base.find((c) => c.valor === valor)?.n ?? 0 }));
  const fuera = base.filter((c) => !orden.includes(c.valor));
  return [...enOrden, ...fuera];
}

/** La mediana, o `null` si no hay datos. No se promedia: un dígito de más no la mueve. */
export function mediana(xs: readonly number[]): number | null {
  if (xs.length === 0) return null;
  const o = [...xs].sort((a, b) => a - b);
  const m = Math.floor(o.length / 2);
  return o.length % 2 === 1 ? o[m] : (o[m - 1] + o[m]) / 2;
}

/** Porcentaje entero, o `null` sin denominador. Nunca un 0 % inventado sobre cero casos. */
export const pct = (n: number, de: number): number | null => (de === 0 ? null : Math.round((n / de) * 100));

/**
 * La brecha de precio entre el producto del cliente y su rival, en %, sobre el precio del rival:
 * `+20` = el del cliente está 20 % más caro. Es la misma cuenta que muestran los dos formularios
 * en pantalla (`cajaBrecha` en moderno, `montaChoque` en tradicional).
 */
export const brecha = (propio: number, rival: number): number => Math.round(((propio - rival) / rival) * 100);

/** Cómo quedó un conjunto de choques de precio: cuántos se pudieron calcular y hacia qué lado. */
export interface ResumenBrechas {
  calculados: number;
  masCaro: number;
  masBarato: number;
  igual: number;
  mediana: number | null;
}

export function resumirBrechas(brechas: readonly number[]): ResumenBrechas {
  return {
    calculados: brechas.length,
    masCaro: brechas.filter((b) => b > 0).length,
    masBarato: brechas.filter((b) => b < 0).length,
    igual: brechas.filter((b) => b === 0).length,
    mediana: mediana(brechas),
  };
}
